import { useNavigate } from "@solidjs/router";
import { createChatClient, serverMessage } from "@storytell/client";
import { dispatchGlobalEvent } from "@storytell/events";
import type { StreamingChatId } from "@storytell/streaming";
import type { Project, ProjectId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StDialog, StDialogCloseButton, StDialogTitle } from "@storytell/ui/dialog";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import dayjs from "dayjs";
import { TbAlertCircle, TbArrowRight, TbCheck, TbPlus, TbSearch } from "solid-icons/tb";
import type { Accessor, Component, Setter } from "solid-js";
import { createSignal, For, Match, onCleanup, Show, Switch } from "solid-js";
import { projectCreatorLabel } from "~/components/projects/projectCreatorLabel";
import { useProjectFilterAndSort } from "~/hooks/useProjectFilterAndSort";
import { useI18n } from "~/i18n/context";
import { getAvailableProjectsForAssetOperations } from "~/lib/projects";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

type ViewState = "select" | "success" | "error";
type ProjectSortOption = "most_recent" | "create_date";

interface MoveChatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedChatIds: StreamingChatId[];
  currentProjectId: ProjectId;
  onSuccess?: () => void;
  onSwitchProject?: () => void;
}

interface SelectViewProps {
  count: number;
  projects: Accessor<Project[]>;
  searchQuery: Accessor<string>;
  setSearchQuery: Setter<string>;
  sortOption: Accessor<ProjectSortOption>;
  setSortOption: Setter<ProjectSortOption>;
  selectedProjectId: Accessor<ProjectId | null>;
  moving: Accessor<boolean>;
  onSelectProject: (projectId: ProjectId) => void;
  onConfirm: () => void;
  onClose: () => void;
  onCreateProject: () => void;
}

const ProjectOption: Component<{
  project: Project;
  selectedProjectId: Accessor<ProjectId | null>;
  onSelect: (projectId: ProjectId) => void;
}> = (props) => {
  const { t } = useI18n();
  const formattedDate = () => {
    if (!props.project.createdAt) return null;
    return dayjs(props.project.createdAt).format("MMM D, YYYY");
  };

  return (
    <button
      type="button"
      onClick={() => props.onSelect(props.project.id)}
      class="flex items-center justify-between rounded-lg border border-primary-border p-3 text-left transition-colors hover:border-blue-500 hover:bg-panel-hover dark:hover:border-blue-500"
      classList={{
        "border-blue-500 dark:border-blue-500 bg-blue-50 dark:bg-blue-950/20":
          props.selectedProjectId() === props.project.id,
      }}
    >
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{props.project.name}</div>
        <Show when={props.project.description}>
          <div class="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{props.project.description}</div>
        </Show>
        <div class="mt-1.5 flex items-center gap-3 text-xs text-muted-text">
          <Show when={formattedDate()}>
            {(date) => (
              <span>
                {t("label.created")}: {date()}
              </span>
            )}
          </Show>
          <Show when={props.project.createdByName}>
            {(name) => (
              <span>
                {t("modal_project_created_by")}: {projectCreatorLabel(name(), t("project_created_by_storytell"))}
              </span>
            )}
          </Show>
        </div>
      </div>
      <Show when={props.selectedProjectId() === props.project.id}>
        <TbCheck class="ml-2 size-5 shrink-0 text-blue-600 dark:text-blue-400" />
      </Show>
    </button>
  );
};

const SelectView: Component<SelectViewProps> = (props) => {
  const { t } = useI18n();

  return (
    <>
      <StDialogTitle>
        <Show when={props.count === 1} fallback={t("move_chats.title_plural", { count: props.count })}>
          {t("move_chats.title", { count: props.count })}
        </Show>
      </StDialogTitle>
      <StDialogCloseButton />

      <div class="space-y-4">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          <Show when={props.count === 1} fallback={t("move_chats.description_plural", { count: props.count })}>
            {t("move_chats.description", { count: props.count })}
          </Show>
        </p>

        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">{t("modal_available_projects")}</h3>
            <div class="flex items-center gap-2">
              <span class="text-xs text-secondary-text">{t("label.sort_by")}</span>
              <StDropdown
                placement="bottom-end"
                contentProps={{ class: "z-[120]" }}
                content={
                  <div>
                    <StDropdownItem
                      onClick={() => props.setSortOption("most_recent")}
                      class="cursor-pointer rounded-md px-2 py-1 hover:bg-tile-hover"
                      classList={{ "bg-panel-hover": props.sortOption() === "most_recent" }}
                    >
                      {t("label.most_recent")}
                    </StDropdownItem>
                    <StDropdownItem
                      onClick={() => props.setSortOption("create_date")}
                      class="cursor-pointer rounded-md px-2 py-1 hover:bg-tile-hover"
                      classList={{ "bg-panel-hover": props.sortOption() === "create_date" }}
                    >
                      {t("modal_project_sort_create_date")}
                    </StDropdownItem>
                  </div>
                }
              >
                <StDropdownTrigger
                  type="button"
                  class="rounded-md border border-primary-border bg-tile px-2 py-1 text-xs text-secondary-text hover:bg-tile-hover hover:text-primary-text focus:ring-2 focus:ring-brand focus:outline-none"
                >
                  <Show when={props.sortOption() === "most_recent"} fallback={t("modal_project_sort_create_date")}>
                    {t("label.most_recent")}
                  </Show>
                </StDropdownTrigger>
              </StDropdown>
              <StButton
                icon={TbPlus}
                class="shrink-0 bg-brand text-sm text-on-brand hover:bg-brand-hover"
                onClick={props.onCreateProject}
              >
                {t("projects_modal_add_project")}
              </StButton>
            </div>
          </div>

          <div class="relative">
            <TbSearch class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-secondary-text" />
            <input
              type="text"
              value={props.searchQuery()}
              onInput={(event) => props.setSearchQuery(event.currentTarget.value)}
              placeholder={t("modal_project_search_placeholder")}
              class="w-full rounded-lg border border-primary-border bg-tile py-2 pr-3 pl-9 text-sm text-primary-text placeholder-secondary-text focus:ring-2 focus:ring-brand focus:outline-none"
              aria-label={t("modal_project_search_placeholder")}
            />
          </div>

          <div class="grid max-h-100 grid-cols-1 gap-2 overflow-y-auto">
            <Show
              when={props.projects().length > 0}
              fallback={
                <div class="flex flex-col items-center justify-center gap-4 py-8">
                  <p class="text-center text-sm text-gray-500 dark:text-gray-400">
                    {t("modal_no_other_projects_available")}
                  </p>
                  <StButton
                    icon={TbPlus}
                    class="bg-brand text-on-brand hover:bg-brand-hover"
                    onClick={props.onCreateProject}
                  >
                    {t("projects_modal_add_project")}
                  </StButton>
                </div>
              }
            >
              <For each={props.projects()}>
                {(project) => (
                  <ProjectOption
                    project={project}
                    selectedProjectId={props.selectedProjectId}
                    onSelect={props.onSelectProject}
                  />
                )}
              </For>
            </Show>
          </div>
        </div>

        <p class="text-xs text-muted-text">{t("move_chats.warning_scope")}</p>

        <div class="flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <StButton onClick={props.onClose} disabled={props.moving()}>
            {t("common.cancel")}
          </StButton>
          <StButton
            onClick={props.onConfirm}
            disabled={!props.selectedProjectId() || props.moving() || props.projects().length === 0}
            icon={TbArrowRight}
            class="bg-blue-600 text-white hover:bg-blue-700"
          >
            <Show
              when={props.moving()}
              fallback={
                <Show when={props.count === 1} fallback={t("move_chats.confirm_plural", { count: props.count })}>
                  {t("move_chats.confirm")}
                </Show>
              }
            >
              {t("move_chats.moving")}
            </Show>
          </StButton>
        </div>
      </div>
    </>
  );
};

const SuccessView: Component<{
  count: number;
  destinationProject: Accessor<Project | null>;
  onStay: () => void;
  onSwitch: () => void;
}> = (props) => {
  const { t } = useI18n();
  const projectName = () => props.destinationProject()?.name ?? "";

  return (
    <>
      <StDialogTitle>{t("move_chats.success_title")}</StDialogTitle>
      <StDialogCloseButton />
      <div class="space-y-4">
        <div class="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/20">
          <div class="flex items-start gap-3">
            <TbCheck class="mt-0.5 size-5 shrink-0 text-green-600 dark:text-green-400" />
            <p class="text-sm text-green-900 dark:text-green-100">
              <Show
                when={props.count === 1}
                fallback={t("move_chats.success_message_plural", {
                  count: props.count,
                  projectName: projectName(),
                })}
              >
                {t("move_chats.success_message", {
                  count: props.count,
                  projectName: projectName(),
                })}
              </Show>
            </p>
          </div>
        </div>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          {t("move_chats.go_to_project", { projectName: projectName() })}
        </p>
        <div class="flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <StButton onClick={props.onStay}>{t("button_stay_here")}</StButton>
          <StButton onClick={props.onSwitch} icon={TbArrowRight} class="bg-blue-600 text-white hover:bg-blue-700">
            {t("button_go_to_project")}
          </StButton>
        </div>
      </div>
    </>
  );
};

const ErrorView: Component<{ message: Accessor<string | null>; onClose: () => void }> = (props) => {
  const { t } = useI18n();

  return (
    <>
      <StDialogTitle>{t("move_chats.error_title")}</StDialogTitle>
      <StDialogCloseButton />
      <div class="space-y-4">
        <div class="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/20">
          <div class="flex items-start gap-3">
            <TbAlertCircle class="mt-0.5 size-5 shrink-0 text-red-600 dark:text-red-400" />
            <p class="text-sm font-medium text-red-900 dark:text-red-100">
              {props.message() ?? t("move_chats.error_generic")}
            </p>
          </div>
        </div>
        <p class="text-sm text-gray-600 dark:text-gray-400">{t("move_chats.error_suggestion")}</p>
        <div class="flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <StButton onClick={props.onClose} class="bg-blue-600 text-white hover:bg-blue-700">
            {t("common.close")}
          </StButton>
        </div>
      </div>
    </>
  );
};

export const MoveChatModal: Component<MoveChatModalProps> = (props) => {
  const [view, setView] = createSignal<ViewState>("select");
  const [selectedProjectId, setSelectedProjectId] = createSignal<ProjectId | null>(null);
  const [moving, setMoving] = createSignal(false);
  const [destinationProject, setDestinationProject] = createSignal<Project | null>(null);
  const [movedChatCount, setMovedChatCount] = createSignal(0);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);
  const wire = useWire();
  const navigate = useNavigate();
  // Chats move same-org only: the mover rejects a destination in another
  // organization, so those never reach the picker.
  const currentOrganizationId = () => wire.projects.get(props.currentProjectId)?.organizationId;
  const availableProjects = () =>
    getAvailableProjectsForAssetOperations(wire, props.currentProjectId).filter(
      (project) => project.organizationId === currentOrganizationId()
    );
  const { filteredAndSortedProjects, searchQuery, setSearchQuery, sortOption, setSortOption } =
    useProjectFilterAndSort(availableProjects);
  let resetTimer: ReturnType<typeof setTimeout> | undefined;

  onCleanup(() => {
    if (resetTimer) clearTimeout(resetTimer);
  });

  const close = () => {
    props.onOpenChange(false);
    resetTimer = setTimeout(() => {
      setView("select");
      setSelectedProjectId(null);
      setDestinationProject(null);
      setMovedChatCount(0);
      setErrorMessage(null);
      setSearchQuery("");
      setSortOption("most_recent");
    }, 200);
  };

  const createProject = () => {
    close();
    navigate("/projects?openCreate=1");
  };

  const confirm = async () => {
    const destinationProjectId = selectedProjectId();
    if (!destinationProjectId) return;

    setMoving(true);
    setErrorMessage(null);
    try {
      await createChatClient(wire.auth.token()).moveChats(
        props.selectedChatIds.map((chatId) => ({ chatId, projectId: destinationProjectId }))
      );
      setDestinationProject(wire.projects.get(destinationProjectId) ?? null);
      setMovedChatCount(props.selectedChatIds.length);
      trackFeature(
        props.selectedChatIds.length > 1
          ? { feature: "chat_list", action: "bulk_move", via: "button" }
          : { feature: "chat.move", via: "button" }
      );
      dispatchGlobalEvent("chat-moved", {
        chatIds: props.selectedChatIds,
        sourceProjectId: props.currentProjectId,
        destinationProjectId,
      });
      props.onSuccess?.();

      const navigationState = wire.navigation.state();
      const primaryChatId = navigationState.primaryId;
      const sideChatId = navigationState.sideId;
      const isSourceProject = navigationState.projectId === props.currentProjectId;
      const primaryChatMoved =
        isSourceProject &&
        primaryChatId !== undefined &&
        props.selectedChatIds.includes(primaryChatId as StreamingChatId);
      const sideChatMoved =
        isSourceProject && sideChatId !== undefined && props.selectedChatIds.includes(sideChatId as StreamingChatId);
      const sideChatStayedBehind = sideChatId?.startsWith("chat_") === true && !sideChatMoved;

      if (primaryChatMoved && primaryChatId !== undefined) {
        wire.navigation.navigateToPrimary(primaryChatId, {
          projectId: destinationProjectId,
          preserveSidePanel: !sideChatStayedBehind,
        });
        close();
      } else {
        if (sideChatMoved) {
          wire.navigation.closeSidePanel();
        }
        setView("success");
      }
    } catch (error) {
      console.error("Failed to move chats", error);
      if (
        error instanceof Error &&
        "status" in error &&
        typeof error.status === "number" &&
        error.status >= 400 &&
        error.status < 500
      ) {
        setErrorMessage(serverMessage(error.message));
      }
      setView("error");
    } finally {
      setMoving(false);
    }
  };

  const switchToDestination = () => {
    const destinationProjectId = selectedProjectId();
    if (!destinationProjectId) return;
    props.onSwitchProject?.();
    close();
    wire.userContext.switchToProject(destinationProjectId);
  };

  return (
    <StDialog
      open={props.open}
      onOpenChange={(open) => {
        if (open) {
          props.onOpenChange(true);
          return;
        }
        close();
      }}
      overlayProps={{ class: "fixed z-[110] inset-0 bg-black/50 dark:bg-white/10" }}
      wrapperProps={{ class: "fixed z-[110] inset-0 flex items-center justify-center pointer-events-none" }}
      content={
        <Switch>
          <Match when={view() === "select"}>
            <SelectView
              count={props.selectedChatIds.length}
              projects={filteredAndSortedProjects}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              sortOption={sortOption}
              setSortOption={setSortOption}
              selectedProjectId={selectedProjectId}
              moving={moving}
              onSelectProject={setSelectedProjectId}
              onConfirm={() => void confirm()}
              onClose={close}
              onCreateProject={createProject}
            />
          </Match>
          <Match when={view() === "success"}>
            <SuccessView
              count={movedChatCount()}
              destinationProject={destinationProject}
              onStay={close}
              onSwitch={switchToDestination}
            />
          </Match>
          <Match when={view() === "error"}>
            <ErrorView message={errorMessage} onClose={close} />
          </Match>
        </Switch>
      }
    />
  );
};
